/**
 * Telling stored state apart from pending state, in the column-mapping modals.
 *
 * Both modals let you edit a grid and save it in one go, and until now nothing on screen
 * said which rows were already stored and which were only proposed. That is how an
 * inherited business key — pre-filled, wearing a settled-looking green "Inherited" pill —
 * could be mistaken for something already saved, and left unsaved.
 *
 * The rule here is deliberately blunt: compare each row against what was loaded. Anything
 * that differs, or was not there at all, is PENDING and says so.
 */

/** What happened to a row since it was loaded. `null` means it matches what is stored. */
export type PendingKind = "new" | "changed" | "removed";

/** A row's comparable content, reduced to one string. */
export type RowSignature = string;

/**
 * Build a signature from the fields that count as "content".
 *
 * `null`, `undefined` and `""` all mean "empty" here — the modals move between them freely
 * (a cleared input becomes `""`, an unmapped column arrives as `null`), and treating those
 * as different would mark rows pending for no reason a user could see.
 */
export function signatureOf(values: ReadonlyArray<unknown>): RowSignature {
  return values
    .map((value) => {
      if (value === null || value === undefined) {
        return "";
      }
      if (typeof value === "boolean") {
        return value ? "1" : "0";
      }
      return String(value).trim();
    })
    .join("");
}

/** Signatures of the rows as loaded, keyed the same way the grid keys them. */
export function snapshotSignatures<TRow>(
  rows: ReadonlyArray<TRow>,
  keyOf: (row: TRow) => string,
  signatureOfRow: (row: TRow) => RowSignature
): Map<string, RowSignature> {
  const snapshot = new Map<string, RowSignature>();
  rows.forEach((row) => snapshot.set(keyOf(row), signatureOfRow(row)));
  return snapshot;
}

/** How this row differs from the snapshot, if at all. */
export function pendingKindOf(
  key: string,
  signature: RowSignature,
  snapshot: ReadonlyMap<string, RowSignature>
): PendingKind | null {
  if (!snapshot.has(key)) {
    return "new";
  }
  return snapshot.get(key) === signature ? null : "changed";
}

/**
 * Every pending row, including ones that have disappeared since loading.
 *
 * Returned as a Map so a caller can both count and look up per row without walking twice.
 */
export function pendingChanges<TRow>(
  rows: ReadonlyArray<TRow>,
  snapshot: ReadonlyMap<string, RowSignature>,
  keyOf: (row: TRow) => string,
  signatureOfRow: (row: TRow) => RowSignature
): Map<string, PendingKind> {
  const pending = new Map<string, PendingKind>();
  const seen = new Set<string>();
  rows.forEach((row) => {
    const key = keyOf(row);
    seen.add(key);
    const kind = pendingKindOf(key, signatureOfRow(row), snapshot);
    if (kind) {
      pending.set(key, kind);
    }
  });
  snapshot.forEach((_signature, key) => {
    if (!seen.has(key)) {
      pending.set(key, "removed");
    }
  });
  return pending;
}

/** "3 unsaved changes" — plain, countable, and the same words in both modals. */
export function describePending(count: number): string {
  if (count <= 0) {
    return "No unsaved changes";
  }
  return count === 1 ? "1 unsaved change" : `${count} unsaved changes`;
}
